import { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Grid, Stack } from "@mui/material";
import { useForm } from "react-hook-form";

import {
  createAdminUser,
  isUserExisting,
  updateUser,
} from "../../../Api/User/User.Api";
import useApiData from "../../../Utils/Hooks/useApiData";
import useLoadingState from "../../../Utils/Hooks/useLodingState";
import useOverlayState from "../../../Utils/Hooks/useOverlayState";
import { SideDrawer } from "../../atoms/Drawer/SideDrawer";
import { PhoneNumber } from "../../atoms/Input/PhoneNumber";
import { SelectInput } from "../../atoms/Input/SelectInput";
import { TextInput } from "../../atoms/Input/TextInput";
import { LoadingButtonType } from "../../atoms/LoadingButton/LoadingButton";
import { NoRecordsFound } from "../../atoms/NoRecordsFound/NoRecordsFound";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { schema } from "./shema";

export const NewUser = ({ close, record, t, OpenDrawer, refetch, mode }) => {
  //state for the confirm dialog box
  const { closeOverlay, OverlayState, openOverlay } = useOverlayState();
  //state for the save button
  const { LoadingState, startLoading, stopLoading } = useLoadingState();
  // trigger server error
  const [isError, setIsError] = useState(false);
  //form control
  const {
    control,
    reset,
    handleSubmit,
    setError,
    setValue,
    watch
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      roleName: "CallAgent",
      phoneNumber: "",
      phoneNumberCountryCode: "lk",
      phoneNumberDialCode: "",
    },
    resolver: yupResolver(schema(t)),
    mode: "all",
  });

  const apiCall = async () => {
    if (mode === "view-edit" && record) {
      const response = await isUserExisting({ code: record.userIdentifier });
      if (response.type == 0) {
        return response;
      } else {
        throw new Error(response.message || "Failed to fetch user data");
      }
    } else return null;
  };
  // check if the user has saved password and entered to the system as an existing user
  const { isLoading, data, error } = useApiData({
    apiCall: mode === "view-edit" && record && apiCall,
    dependency: [record.UserIdentifier],
  });

  // store form values for view and edit mode
  useEffect(() => {
    if (mode === "view-edit" && record) {
      reset({
        firstName: record?.firstName || "",
        lastName: record?.lastName || "",
        email: record?.email || "",
        roleName: record?.roleName || "CallAgent",
        phoneNumber: record?.phoneNumber || "",
        phoneNumberCountryCode: record?.phoneNumberCountryCode || "",
        phoneNumberDialCode: record?.phoneNumberDialCode || ""
      });
    }
    return () => {
      reset();
    };
  }, [record, mode]);
  // form submit event
  const onSubmit = async (data) => {
    if (isError) {
      setIsError(false);
    }
    startLoading({ btnName: "add" });
    if (mode === "view-edit") {
      await updateUser({
        user: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          roleName: record.roleName,
          userCode: record.userIdentifier,
          userId: record.userId,
          phoneNumber: data?.phoneNumber,
          phoneNumberCountryCode: data?.phoneNumberCountryCode,
          phoneNumberDialCode: data?.phoneNumberDialCode
        },
      }).then((response) => {
        if (response.type == 0) {
          refetch();
          if (response.message === "not-affected") {
          } else if (response.message === "email-sent") {
            openOverlay({ OverlayName: "email-sent" });
          } else {
            close();
          }
        } else if (response.type == 2) {
          if (response.message === "email-exist") {
            setError("email", {
              message: t(response.message, { ns: "validation" }),
            });
          } else if (response.message === "email-not-sent") {
            openOverlay({
              OverlayName: "reset-password",
              mode: "fail-email-not-sent",
            });
          } else if (response.message === "phonenum-exist") {
            setError("phoneNumber", {
              message: t(response.message, { ns: "validation" }),
            });
          } else {
            setIsError(response.message || true);
          }
        } else {
          setIsError(true);
          if (record.email !== data?.email) {
            openOverlay({ OverlayName: "reset-password", mode: "fail" });
          }
        }
      });
    } else {
      await createAdminUser({ user: data }).then((response) => {
        if (response.type == 0) {
          refetch();
          reset();
          openOverlay({ OverlayName: "reset-password" });
        } else if (response.type == 2) {
          if (response.message === "email-exist") {
            setError("email", {
              message: t(response.message, { ns: "validation" }),
            });
          } else if (response.message === "email-not-sent") {
            openOverlay({
              OverlayName: "reset-password",
              mode: "fail-email-not-sent",
            });
          } else if (response.message === "phonenum-exist") {
            setError("phoneNumber", {
              message: t(response.message, { ns: "validation" }),
            });
          } else {
            setIsError(response.message || true);
          }
        } else {
          setIsError(true);
        }
      });
    }

    stopLoading();
  };
  return (
    <SideDrawer
      isError={error && mode === "view-edit"}
      isLoading={isLoading}
      openDrawer={OpenDrawer}
      close={close}
      anchorDrawer="right"
      width={"648px"}
      headingText={
        mode === "view-edit"
          ? t("glossary:user.update-user")
          : t("glossary:user.new-user")
      }
    // alert message show when user try to exit during form changes
    // isDirty={isDirty}
    >
      {isError && (
        <NoRecordsFound
          isError={!!isError}
          textPosition={"start"}
          padding={"8px"}
          textColor={"black"}
          label={typeof isError === "string" ? isError : t("validation:server-error")}
        />
      )}

      <Grid
        container
        direction={"column"}
        width={"552px"}
        height={"100%"}
        sx={{ marginTop: "20px" }}
      >
        {
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
              item
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2,1fr)",
                gap: "16px",
              }}
            >
              <TextInput
                required={true}
                label={t("common:form.fname")}
                control={control}
                name={"firstName"}
              />
              <TextInput
                required={true}
                label={t("common:form.lname")}
                control={control}
                name={"lastName"}
              />
              <TextInput
                disabled={!isLoading && data?.message === "existing-user"}
                label={t("common:form.email")}
                placeholder={t("common:placeholder.email")}
                control={control}
                name={"email"}
                required={true}
              />
              <SelectInput
                required={true}
                disabled={true}
                width={"100%"}
                label={t("common:form.role")}
                control={control}
                placeholder={"Select Role"}
                options={[
                  {
                    label: t(`user.CallAgent`, { ns: "glossary" }),
                    key: "CallAgent",
                  },
                  {
                    label: t(`user.SuperAdmin`, { ns: "glossary" }),
                    key: "SuperAdmin",
                  },
                  {
                    label: t(`user.Admin`, { ns: "glossary" }),
                    key: "Admin",
                  },
                ]}
                name={"roleName"}
              />
              <PhoneNumber
                values={watch()}
                setValue={setValue}
                required={true}
                width={"220px"}
                name={"phoneNumber"}
                placeholder={"XX XXXX XXXX"}
                control={control}
                label={t("common:form.mobileNoFirstOnly")}
              />
            </Grid>
            <Stack
              direction={"row"}
              spacing={2}
              width={"100%"}
              justifyContent={"flex-end"}
              marginTop={"19px"}
              alignItems={"center"}
            >
              <Button variant="whiteSquare" onClick={close}>
                {t("common:button.cancel")}
              </Button>
              <LoadingButtonType
                loading={LoadingState.visible}
                variant="primaryBlue"
                type="submit"
              >
                {mode === "view-edit"
                  ? t("user.action.update-profile", { ns: "glossary" })
                  : t("user.action.create-profile", { ns: "glossary" })}
              </LoadingButtonType>
            </Stack>
          </form>
        }
      </Grid>
      {OverlayState.visible && (
        <ConfirmDialog
          isError={
            OverlayState.mode === "fail" ||
            OverlayState.mode === "fail-email-not-sent"
          }
          title={t("user.action.reset-password", { ns: "glossary" })}
          description={
            OverlayState.mode === "fail" ||
              OverlayState.mode === "fail-email-not-sent"
              ? t("validation:password-link-send-fail")
              : t("user.action.reset-password-desc", { ns: "glossary" })
          }
          open={OverlayState?.visible}
          close={() => {
            if (OverlayState.mode !== "fail") {
              close();
            }

            closeOverlay();
          }}
          onOkClick={() => {
            if (OverlayState.mode !== "fail") {
              close();
            }
            closeOverlay();
          }}
        />
      )}
    </SideDrawer>
  );
};
